import React from 'react';
import { createRoot } from 'react-dom/client';
import App from './App.jsx';

// Early build-version check before any rendering. A first visit (no stored
// version) only records the shipped version and renders immediately; only a
// stored version that differs from the shipped one clears storage, caches and
// stale service workers and reloads, exactly once per version (a
// sessionStorage flag guards against reload loops).
const checkLegacyCache = async () => {
  try {
    const buildVersion = document.querySelector('meta[name="build-version"]')?.content;
    if (!buildVersion) return false;

    const storedVersion = localStorage.getItem('build-version');
    if (!storedVersion || storedVersion === buildVersion) {
      localStorage.setItem('build-version', buildVersion);
      return false;
    }

    if (sessionStorage.getItem('build-version-reloaded') === buildVersion) {
      // This session already reloaded for this version; never loop.
      localStorage.setItem('build-version', buildVersion);
      return false;
    }

    localStorage.clear();
    sessionStorage.clear();
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map(registration => registration.unregister()));
    }
    const cacheKeys = await caches.keys();
    await Promise.all(cacheKeys.map(key => caches.delete(key)));
    localStorage.setItem('build-version', buildVersion);
    sessionStorage.setItem('build-version-reloaded', buildVersion);
    window.location.reload();
    return true;
  } catch (error) {
    console.error('Cache refresh failed:', error);
    return false;
  }
};

// This app ships no service worker: any registration is a leftover of the old build
// and keeps serving its years-old cached start page. It is removed on EVERY load
// (not only on a version change — a cleared localStorage otherwise let it survive),
// and when one still controlled this load, one guarded reload fetches the real page.
const killLegacyServiceWorkers = async () => {
  try {
    if (!('serviceWorker' in navigator)) return false;
    const registrations = await navigator.serviceWorker.getRegistrations();
    if (!registrations.length) return false;
    await Promise.all(registrations.map(registration => registration.unregister()));
    const cacheKeys = await caches.keys();
    await Promise.all(cacheKeys.map(key => caches.delete(key)));
    if (navigator.serviceWorker.controller && !sessionStorage.getItem('sw-cleared')) {
      sessionStorage.setItem('sw-cleared', '1');
      window.location.reload();
      return true;
    }
  } catch (error) {
    console.error('Legacy service worker cleanup failed:', error);
  }
  return false;
};

// Render app only after cache check
const renderApp = async () => {
  if (await killLegacyServiceWorkers()) return;
  const needsReload = await checkLegacyCache();
  if (!needsReload) {
    document.getElementById('seo-static')?.remove();
    const container = document.getElementById('root');
    const root = createRoot(container);
    root.render(<App />);
  }
};

renderApp();
